import React, { createContext, type ReactNode, useContext } from 'react';

import type { ExtensionParams } from '@atlaskit/editor-common/extensions';
import type { ExtensionMode } from '@atlassian/company-hub-common';

import type { HubConnectExtensionParameters } from '../../types';

/**
 * Shape of the Connect renderer that the host (the `@atlassian/hub` package)
 * injects. The renderer is responsible for resolving the Connect module
 * for the targeted hub slot and rendering its iframe.
 *
 * Keeping the renderer behind a context boundary lets
 * `@atlassian/hub-connect-extension` stay free of any direct
 * `@atlaskit/connect-module-core` dependency, mirrors how Help Center
 * isolates its Connect integration, and keeps this package cheap to
 * lazy-load.
 */
export type HubConnectRenderer = (props: {
	contentId: string;
	extensionNode: ExtensionParams<HubConnectExtensionParameters>;
	mode: ExtensionMode;
}) => React.JSX.Element | null;

const HubConnectRendererContext = createContext<HubConnectRenderer | undefined>(undefined);

export type HubConnectRendererProviderProps = {
	children: ReactNode;
	renderer: HubConnectRenderer | undefined;
};

export const HubConnectRendererProvider = ({
	children,
	renderer,
}: HubConnectRendererProviderProps): React.JSX.Element => (
	<HubConnectRendererContext.Provider value={renderer}>
		{children}
	</HubConnectRendererContext.Provider>
);

export const useHubConnectRenderer = (): HubConnectRenderer | undefined =>
	useContext(HubConnectRendererContext);
